// ── Import picker overlay shell (C1449) ──
//
// Shared chrome for every single-item import picker reachable from the New Task modal's
// Import ▾ menu (Trello cards — C1448, Gmail threads — C1449, Jira issues — C1450).
// Deliberately NOT built on ui.js's openModal()/closeModal(): openModal() unconditionally
// calls closeModal() first (single #modal-overlay, single _modalOnClose slot — see ui.js),
// so reusing it here would tear down the still-open New Task modal and fire its onClose,
// which releases the reserved task_key and destroys the half-filled form. This is its own
// body-appended, fixed-position overlay instead — same shape as components/reauth-modal.js
// — stacked above the modal (z-index 260, see styles.css) so both stay mounted at once.
//
// One module-level singleton across ALL import pickers (not one per picker kind): opening
// any import picker closes any other that's still open. Mirrors the decision embed-menu.js
// already made for its own Embed ▾ / Import ▾ dropdown pair (_closeOpenMenu) — see
// ai/architecture/tt-web-board.md's C1448 section for why that's structural, not a new
// module's problem to re-solve. Extracted from trello-card-picker.js (C1448); that file's
// own overlay/backdrop/header/Escape/mousedown-guard region was already 100% Trello-free.

import { escapeHtml } from '../utils.js';

const esc = escapeHtml;

let _picker = null; // module singleton — one import picker overlay at a time, any kind

// createImportPicker({ title }) -> handle
//   handle.body               -> Element, the swappable step content — write via setBody()
//   handle.panel              -> Element, the whole dialog panel (for callers that need it)
//   handle.setBody(html)      -> replace the entire step body
//   handle.statusHtml(text)   -> escaped <p> markup for a neutral status line
//   handle.errorHtml(text)    -> escaped <p> markup for a fatal (whole-body) error
//   handle.showInlineError(text) -> prepend a dismissable-on-retry error above the current body
//   handle.onWindowFocusOnce(fn) -> run fn once on the next window focus (OAuth-return retry);
//                                   calling again replaces any still-pending registration
//   handle.close()            -> tear down: listeners, DOM, singleton slot
//   handle.destroyed          -> getter, true once close() has run
export function createImportPicker({ title }) {
  closeImportPicker(); // opening any import picker closes any other — structural, see header

  const state = { destroyed: false };

  const overlay = document.createElement('div');
  overlay.className = 'import-picker-overlay';
  overlay.innerHTML = `
    <div class="import-picker-backdrop"></div>
    <div class="import-picker-panel" role="dialog" aria-modal="true" aria-labelledby="import-picker-title">
      <div class="import-picker-header">
        <h3 class="import-picker-title" id="import-picker-title">${esc(title)}</h3>
        <button type="button" class="btn btn-ghost btn-sm import-picker-close" aria-label="Close">&times;</button>
      </div>
      <div class="import-picker-body"></div>
    </div>`;
  document.body.appendChild(overlay);

  const panel = overlay.querySelector('.import-picker-panel');
  const body = overlay.querySelector('.import-picker-body');

  // Any mousedown inside the panel must not blur the New Task modal's description field
  // (board.js's blur handler swaps in the C1446 rendered preview) — EXCEPT on the picker's
  // own form controls, which preventDefault() would otherwise leave mouse-dead: it suppresses
  // the browser's default focus/caret placement for the whole event, so a search <input> or a
  // <select> under this guard could never be clicked into, only tabbed to (found while porting
  // this out of trello-card-picker.js — C1448 shipped with its search box and both Step 2
  // <select>s mouse-dead). Rows stay excluded on purpose: they're <div tabindex="0"
  // role="button"> driven by a delegated click handler that needs no focus.
  panel.addEventListener('mousedown', (e) => {
    if (e.target.closest('input, select, textarea')) return;
    e.preventDefault();
  });

  overlay.addEventListener('mousedown', (e) => {
    if (e.target === overlay || e.target.classList.contains('import-picker-backdrop')) close();
  });
  overlay.querySelector('.import-picker-close').addEventListener('click', close);

  // Capture-phase + stopPropagation — identical rationale to embed-menu.js's dropdown
  // Escape handler: ui.js's openModal() registered an unconditional BUBBLE-phase
  // `Escape -> closeModal()` on document when the New Task modal opened, and only removes
  // it when it fires (stale handlers can accumulate across opens). Without capture here,
  // one Escape would close the picker AND the New Task modal underneath it, releasing the
  // reserved task_key. First Escape closes the picker; a second Escape (picker already
  // gone) reaches the modal's own handler normally.
  const onDocKeydown = (e) => {
    if (e.key === 'Escape') { e.stopPropagation(); close(); }
  };
  document.addEventListener('keydown', onDocKeydown, true);

  let onWindowFocus = null;

  function close() {
    state.destroyed = true;
    document.removeEventListener('keydown', onDocKeydown, true);
    if (onWindowFocus) { window.removeEventListener('focus', onWindowFocus); onWindowFocus = null; }
    overlay.remove();
    if (_picker === handle) _picker = null;
  }

  function setBody(html) { body.innerHTML = html; }

  function statusHtml(text) {
    return `<p class="import-picker-status">${esc(text)}</p>`;
  }

  function errorHtml(text) {
    return `<p class="import-picker-error">${esc(text)}</p>`;
  }

  // Prepend an inline error above whatever's currently rendered, without touching it — a
  // plain setBody(errorHtml + body.innerHTML) would re-parse the existing form from a
  // string and silently drop its listeners. Removes any prior error first so retries don't
  // stack duplicate messages.
  function showInlineError(text) {
    body.querySelector('.import-picker-error')?.remove();
    body.insertAdjacentHTML('afterbegin', errorHtml(text));
  }

  // One-shot OAuth-return retry: run fn() the next time this window regains focus (the user
  // tabbed back after finishing auth in the popup). Owning the listener's removal here (both
  // on fire and on close()) means a picker never has to hand-roll this leak itself.
  function onWindowFocusOnce(fn) {
    if (onWindowFocus) window.removeEventListener('focus', onWindowFocus);
    onWindowFocus = () => {
      window.removeEventListener('focus', onWindowFocus);
      onWindowFocus = null;
      if (!state.destroyed) fn();
    };
    window.addEventListener('focus', onWindowFocus);
  }

  const handle = {
    body, panel,
    setBody, statusHtml, errorHtml, showInlineError, onWindowFocusOnce, close,
    get destroyed() { return state.destroyed; },
  };
  _picker = handle;
  return handle;
}

// Orphan-overlay guard — call unconditionally from any host's teardown (board.js's
// openCreateTaskModal onClose) alongside embedControls.destroy(). Covers every import
// picker kind through the one shared singleton — a host never needs a separate guard call
// per picker kind, including Jira (C1450). If the New Task modal closes while a
// picker is still open (Escape reaching the modal underneath, backdrop click, route
// navigation, renderActiveView()'s sub-view cleanup calling closeModal()), nothing else
// tears the picker down — it would otherwise survive as a full-viewport overlay with a live
// capture-phase Escape handler that swallows every Escape on the page.
export function closeImportPicker() {
  if (_picker) _picker.close();
}

// ── C1551: shared "not configured" setup notice ──
//
// api.js sets err.status + err.body = <full parsed JSON> on any non-2xx (see api.js's !res.ok
// branch). A 503 from an unconfigured integration (routes/gmail.js, trello.js, jira.js —
// lib/integration-config.js's requireIntegrationConfig()) carries
// { error: '<service>_not_configured', service, missing: [...], docs }. Every picker's own
// error map (GMAIL_ERRORS/JIRA_ERRORS, or trello-card-picker.js's raw err.message) predates
// this token and would either fall into a generic ">= 500 -> reconnect" branch (misleading —
// there is nothing to reconnect) or print the token itself. Recognize it here once so all
// three pickers render the same notice instead of re-deriving this per picker.

// notConfiguredFrom(err) -> { service, missing, docs } | null
export function notConfiguredFrom(err) {
  const body = err && err.status === 503 ? err.body : null;
  const code = body && body.error;
  if (typeof code !== 'string' || !/^[a-z]+_not_configured$/.test(code)) return null;
  if (!Array.isArray(body.missing) || !body.missing.length) return null;
  return { service: body.service || code.replace(/_not_configured$/, ''), missing: body.missing, docs: body.docs };
}

// setupNoticeHtml({ service, missing, docs }) -> fatal whole-body markup (esc() pieces only —
// no raw HTML ever reaches this string) naming exactly which env vars are missing, for
// setBody(). Capitalizes service for display; escapes every dynamic piece independently.
export function setupNoticeHtml({ service, missing, docs }) {
  const label = service ? service[0].toUpperCase() + service.slice(1) : 'This integration';
  const items = missing.map((name) => `<li><code>${esc(name)}</code></li>`).join('');
  return `
    <p class="import-picker-error" style="margin:0 0 8px">${esc(label)} import isn't configured on this server yet.</p>
    <p style="margin:0 0 4px;font-size:.85rem">Missing environment variable${missing.length > 1 ? 's' : ''}:</p>
    <ul style="margin:0 0 12px;padding-left:20px;font-size:.85rem">${items}</ul>
    ${docs ? `<p style="margin:0;font-size:.8rem;color:var(--c-overlay1)">See <code>${esc(docs)}</code> for setup steps, then restart the API.</p>` : ''}`;
}
